import { z } from 'zod'
import { openingHoursEntrySchema } from '@/features/website-builder/schema/elements'
import {
  languageCodeSchema,
  localizedTextSchema
} from '@/features/website-builder/schema/localized'
import {
  isPlausiblePhone,
  isValidNip,
  isValidRegon
} from '@/shared/utils/polishBusinessIds'
import { DEFAULT_CONTENT_LANGUAGE, WEEKDAY_COUNT } from '../config'

/*
  `openingHoursEntrySchema` and `localizedTextSchema` are imported from
  website-builder, which is now the CONSUMER of these facts rather than their
  owner — the dependency points the wrong way round. The right home is a
  `src/shared/schema/`, which does not exist, and creating it for two schemas is
  over-building today. Worth moving the day a third feature needs either.
*/

/**
 * The legal forms a Polish venue is likely to be, as a closed set.
 *
 * A select rather than free text because this ends up on an invoice and, later,
 * in the Viva payout account, where a typed string is a rejected application.
 * `other` exists so the list is never a reason someone cannot finish the form.
 */
export const LEGAL_FORMS = [
  'soleTrader',
  'civilPartnership',
  'generalPartnership',
  'limitedPartnership',
  'llc',
  'jointStock',
  'cooperative',
  'other'
] as const

export type LegalForm = (typeof LEGAL_FORMS)[number]

/*
  Every optional text field is `.or(z.literal(''))` rather than `.optional()`.
  DynamicForm seeds text inputs with '' and not undefined, so a bare `.optional()`
  fails validation on a field the owner never touched.
*/
const optionalText = (max: number) => z.string().trim().max(max).or(z.literal(''))

/*
  What a venue cannot go without: the guest app, the website and an invoice
  all print its name, address and phone. Checked when the restaurant section
  is saved, so a venue set up before these were required still opens the
  panel without an error, and its next Save asks for what is missing.
*/
const requiredText = (max: number) => z.string().trim().min(1, 'required').max(max)

/** A tax identifier is either blank or real; a half-typed one is neither. */
const nipSchema = z
  .string()
  .trim()
  .refine(value => value === '' || isValidNip(value), { message: 'invalid-nip' })

const regonSchema = z
  .string()
  .trim()
  .refine(value => value === '' || isValidRegon(value), {
    message: 'invalid-regon'
  })

const phoneSchema = z
  .string()
  .trim()
  .min(1, 'required')
  .refine(isPlausiblePhone, { message: 'invalid-phone' })

/**
 * `https` only, and only because a link the owner pastes is rendered on a public
 * page and opened in a new tab. `http` would be downgraded by the browser anyway
 * and `javascript:` must never survive a paste.
 */
const httpsUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .refine(value => value === '' || /^https:\/\/\S+$/.test(value), {
    message: 'invalid-url'
  })

export const restaurantIdentitySchema = z.object({
  /** The name guests see. Not translated: a restaurant's name is its name. */
  name: requiredText(120),
  legalName: optionalText(200),
  nip: nipSchema,
  regon: regonSchema,
  legalForm: z.enum(LEGAL_FORMS).optional(),
  tagline: localizedTextSchema,
  description: localizedTextSchema
})

/*
  Any country's shape, not only Poland's 50-101: the address has no country,
  addresses picked from Google earlier include foreign ones (10115, SW1A 1AA),
  and the address is checked as one draft, so a code refused here kept the
  courier note and the time zone from saving too. A Polish venue's bare digits
  get their dash on save instead (settingsForm.ts).
*/
const POSTAL_CODE_SHAPE = /^[A-Za-z0-9][A-Za-z0-9 -]{1,8}[A-Za-z0-9]$/

/**
 * Where the venue is, as the owner types it. Its place on Google is a
 * separate connection (`features/google-location`), so nothing here is
 * looked up, locked or rationed.
 */
export const restaurantAddressSchema = z.object({
  street: requiredText(160),
  buildingNumber: requiredText(24),
  postalCode: z
    .string()
    .trim()
    .min(1, 'required')
    .refine(value => POSTAL_CODE_SHAPE.test(value), {
      message: 'invalid-postal-code'
    }),
  city: requiredText(80),
  // The opening hours are read in it.
  timezone: requiredText(64),
  unitNumber: optionalText(24),
  deliveryNotes: optionalText(280)
})

export const restaurantContactSchema = z.object({
  phone: phoneSchema,
  email: z.string().trim().max(160).email('invalid-email').or(z.literal('')),
  websiteUrl: httpsUrlSchema,
  facebookUrl: httpsUrlSchema,
  instagramUrl: httpsUrlSchema
})

/** Exactly seven rows: the grid is a week, not a list of exceptions. */
export const openingHoursSchema = z
  .array(openingHoursEntrySchema)
  .length(WEEKDAY_COUNT)

export const restaurantSettingsSchema = z.object({
  identity: restaurantIdentitySchema,
  address: restaurantAddressSchema,
  contact: restaurantContactSchema,
  hours: openingHoursSchema,
  contentLanguage: languageCodeSchema.default(DEFAULT_CONTENT_LANGUAGE)
})

export type RestaurantIdentity = z.infer<typeof restaurantIdentitySchema>
export type RestaurantAddress = z.infer<typeof restaurantAddressSchema>
export type RestaurantContact = z.infer<typeof restaurantContactSchema>
export type OpeningHours = z.infer<typeof openingHoursSchema>
export type RestaurantSettings = z.infer<typeof restaurantSettingsSchema>
